import React, { useState } from "react";
import PageHeader from "../components/ui/PageHeader";
import RefreshButton from "../components/ui/RefreshButton";
import SearchInput from "../components/ui/SearchInput";
import Pagination from "../components/ui/Pagination";
import TableSkeleton from "../components/ui/TableSkeleton";
import EmptyState from "../components/ui/EmptyState";
import ErrorState from "../components/ui/ErrorState";
import ProjectFilters from "../components/project/ProjectFilters";
import ProjectsTable from "../components/project/ProjectsTable";
import { useProjects } from "../hooks/useProjects";
import { useDebouncedValue } from "../hooks/useDebouncedValue";
import { ALL_TYPES, DEFAULT_STATUS } from "../domain/projects";
import { DEFAULT_PAGE_SIZE, totalPagesOf } from "../lib/pagination";
import { remember } from "../lib/projectNameCache";
import "./Projects.css";

const SKELETON_COLUMNS = [
  { key: "name", label: "Projeto", bars: 2 },
  { key: "type", label: "Tipo", className: "col-type" },
  { key: "situation", label: "Situação", className: "col-situation" },
  { key: "status", label: "Status", className: "col-status" },
  { key: "coordinator", label: "Coordenador", className: "col-coordinator" },
  { key: "updated", label: "Atualizado", className: "col-upd" },
  { key: "go", className: "col-go", bars: 0 },
];

function Projects() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [type, setType] = useState(ALL_TYPES);
  const [status, setStatus] = useState(DEFAULT_STATUS);
  const [situations, setSituations] = useState([]);
  const [coordinators, setCoordinators] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const search = useDebouncedValue(searchTerm.trim());

  const { projects, total, loading, error, reload } = useProjects({
    page,
    pageSize,
    search,
    type,
    status,
    situations,
    coordinators,
  });
  const totalPages = totalPagesOf(total, pageSize);

  const applyFilters = ({
    type: nextType,
    status: nextStatus,
    situations: nextSituations,
    coordinators: nextCoordinators,
  }) => {
    setType(nextType);
    setStatus(nextStatus);
    setSituations(nextSituations);
    setCoordinators(nextCoordinators);
    setPage(1);
  };

  const changeSearch = (nextTerm) => {
    setSearchTerm(nextTerm);
    setPage(1);
  };

  const clearFilters = () => {
    setType(ALL_TYPES);
    setStatus(DEFAULT_STATUS);
    setSituations([]);
    setCoordinators([]);
    setSearchTerm("");
    setPage(1);
  };

  const hasProjects = projects.length > 0;

  return (
    <>
      <PageHeader title="Projetos" />

      <div className="filters">
        <div className="filters-lead">
          <SearchInput
            value={searchTerm}
            onChange={changeSearch}
            label="Buscar projeto"
            placeholder="Buscar projeto…"
          />
          <ProjectFilters
            type={type}
            status={status}
            situations={situations}
            coordinators={coordinators}
            onApply={applyFilters}
          />
        </div>
        <RefreshButton onClick={reload} loading={loading} />
      </div>

      <div className="card data-card">
        {loading && <TableSkeleton columns={SKELETON_COLUMNS} label="Carregando projetos" />}

        {!loading && error && (
          <ErrorState
            title="Não foi possível carregar os projetos"
            message="Verifique sua conexão e tente novamente."
            onRetry={reload}
          />
        )}

        {!loading && !error && !hasProjects && (
          <EmptyState
            icon="folder"
            title="Nenhum projeto encontrado"
            help="Ajuste os filtros de tipo, situação e coordenador ou revise o termo da busca."
            action={
              <button className="btn btn-secondary" type="button" onClick={clearFilters}>
                Limpar filtros
              </button>
            }
          />
        )}

        {!loading && !error && hasProjects && (
          <ProjectsTable
            projects={projects}
            onNavigate={(project) => remember(project.id, project.name)}
          />
        )}
      </div>

      {!loading && !error && hasProjects && (
        <Pagination
          label="Paginação dos projetos"
          page={page}
          totalPages={totalPages}
          total={total}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      )}
    </>
  );
}

export default Projects;
